import React, { useEffect, useMemo, useState } from 'react';
import { Card, Container, Label, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import moment from 'moment';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import AppSelectValidation from 'components/App/SelectValidation';
import AppFlatPickInput from 'components/App/FlatPickInput';
import DownloadIcon from 'assets/images/download.svg';
import flightBookingApi from 'api/flightBookingApi';
import { themeColor } from 'constants/index';
import {
  formatFlightAmount, getLoadErrorText, handleFlightApiError, toSystemOptions,
} from 'constants/flightBooking';
import { formatNumber } from 'utils/currencyFormat';
// ponytail: reuses the ManagerPayment list styles, like the flight booking list.
import pageStyles from 'pages/ManagerPayment/ManagerPayment.module.scss';

import {
  REVENUE_CHANGE_COLUMNS,
  REVENUE_CHANGE_NOTE,
  REVENUE_COLUMNS,
  buildRevenueCsv,
  downloadCsv,
  formatRevenueChangeCell,
  formatRevenuePeriod,
  getRevenueRangeError,
} from './flightRevenue';
import styles from './ManagerFlightRevenue.module.scss';

const PAGE_TITLE = 'Doanh thu vé máy bay';
const breadcrumbList = [{ name: PAGE_TITLE, isActive: true, link: '' }];

const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Theo ngày' },
  { value: 'month', label: 'Theo tháng' },
];

// orderCountKey: the order count shown under the value as "(N đơn)".
const SUMMARY_CARDS = [
  { key: 'ticketCount', label: 'Vé đã xuất', orderCountKey: 'issuedCount' },
  { key: 'partnerTotal', label: 'Tiền trả hãng', isMoney: true },
  { key: 'serviceFee', label: 'Phí dịch vụ Bookese', isMoney: true },
  { key: 'refundedTotal', label: 'Hoàn tiền', isMoney: true, orderCountKey: 'refundedCount' },
  { key: 'net', label: 'Doanh thu ròng', isMoney: true },
];

const HEADS = [
  'Kỳ',
  'Hãng',
  ...REVENUE_COLUMNS.map((column) => column.label),
  ...REVENUE_CHANGE_COLUMNS.map((column) => column.label),
];

const RANGE_OPTIONS = { mode: 'range', showMonths: 2, dateFormat: 'd/m/Y' };

const EMPTY_REVENUE = { currency: 'VND', summary: {}, rows: [] };

const ALL_SYSTEMS = { value: '', label: 'Tất cả hãng' };

const getInitialFilters = () => ({
  from: moment().startOf('month').format('YYYY-MM-DD'),
  to: moment().endOf('month').format('YYYY-MM-DD'),
  system: '',
  groupBy: GROUP_BY_OPTIONS[0].value,
});

const rangeLabel = (from, to) => [from, to]
  .filter(Boolean)
  .map((date) => moment(date).format('DD/MM/YYYY'))
  .join(' đến ');

function ManagerFlightRevenue() {
  const [systems, setSystems] = useState([]);
  const [revenue, setRevenue] = useState(EMPTY_REVENUE);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Filters apply as they change; there is no submit.
  const validation = useFormik({ initialValues: getInitialFilters(), onSubmit: () => {} });
  const { values } = validation;

  useEffect(() => {
    flightBookingApi.getFlightSystems()
      .then((response) => setSystems(response.payload || []))
      .catch(handleFlightApiError);
  }, []);

  useEffect(() => {
    // A range is complete only once both ends are picked.
    if (!values.from || !values.to) return undefined;
    // Never send what BE answers 400 for, and drop the previous range's rows.
    if (getRevenueRangeError(values.from, values.to)) {
      setRevenue(EMPTY_REVENUE);
      setLoadError('');
      return undefined;
    }

    let ignore = false;
    setIsLoading(true);
    flightBookingApi.getFlightRevenue(values)
      .then((response) => {
        if (ignore) return;
        setRevenue({ ...EMPTY_REVENUE, ...response.payload });
        setLoadError('');
      })
      .catch((error) => {
        if (ignore) return;
        setRevenue(EMPTY_REVENUE);
        setLoadError(getLoadErrorText(error, 'doanh thu vé máy bay', 'Vui lòng thử lại.'));
        handleFlightApiError(error);
      })
      .finally(() => !ignore && setIsLoading(false));

    return () => { ignore = true; };
  }, [values]);

  const onSelectSystem = (e) => validation.setFieldValue('system', e.value ?? '');

  const onChangeRange = (dates = []) => {
    const [from = '', to = ''] = dates.map((date) => moment(date).format('YYYY-MM-DD'));
    validation.setValues({ ...values, from, to });
  };

  const systemOptions = useMemo(() => [ALL_SYSTEMS, ...toSystemOptions(systems)], [systems]);
  const systemNames = useMemo(
    () => Object.fromEntries(systems.map(({ code, name }) => [code, name])),
    [systems],
  );

  const { currency, summary, rows } = revenue;
  const rangeError = getRevenueRangeError(values.from, values.to);
  const canExport = !isLoading && rows.length > 0 && !!values.to;

  const formatValue = (value, isMoney) => {
    if (value === undefined || value === null) return '-';
    return isMoney ? formatFlightAmount(value, currency) : formatNumber(value);
  };

  const onExport = () => {
    const systemSuffix = values.system ? `_${values.system}` : '';
    downloadCsv(
      buildRevenueCsv(revenue),
      `doanh-thu-ve-may-bay_${values.from}_${values.to}${systemSuffix}.csv`,
    );
  };

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={HEADS.length} className="text-center p-4">
            <Spinner size="sm" color="primary" />
            {' '}
            Đang tải dữ liệu...
          </td>
        </tr>
      );
    }

    if (!rows.length) {
      return (
        <tr>
          <td colSpan={HEADS.length} className="text-center p-4 text-muted">
            {rangeError || loadError || 'Không có doanh thu vé máy bay trong khoảng này'}
          </td>
        </tr>
      );
    }

    return rows.map((row) => (
      <tr key={`${row.period}-${row.system}`}>
        <td>{formatRevenuePeriod(row.period)}</td>
        <td>{systemNames[row.system] || row.system || '-'}</td>
        {REVENUE_COLUMNS.map((column) => (
          <td key={column.key} className={column.key === 'net' ? styles.Strong : ''}>
            {formatValue(row[column.key], column.isMoney)}
          </td>
        ))}
        {REVENUE_CHANGE_COLUMNS.map((column) => (
          <td key={column.key}>{formatRevenueChangeCell(row, column, currency)}</td>
        ))}
      </tr>
    ));
  };

  return (
    <div className={`page-content ${pageStyles.Page}`}>
      <Meta name={PAGE_TITLE} />
      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <Card className={pageStyles.ContentForm}>
          <div className={pageStyles.FormWrapper}>
            <div className={pageStyles.FilterGroup}>
              <div className={pageStyles.FieldLabel}>Bộ lọc</div>
              <div className={pageStyles.FilterInputsRow}>
                <AppFlatPickInput
                  name="revenueRange"
                  placeholder="Khoảng ngày"
                  className={pageStyles.CommonFlatpickr}
                  value={rangeLabel(values.from, values.to)}
                  options={RANGE_OPTIONS}
                  onChange={onChangeRange}
                />
                <AppSelectValidation
                  name="system"
                  placeholder={ALL_SYSTEMS.label}
                  options={systemOptions}
                  value={values.system}
                  validation={validation}
                  onChange={onSelectSystem}
                  selectedClassName={pageStyles.CommonSelect}
                  isHiddenSearchable
                />
                <div className={styles.GroupBy}>
                  {GROUP_BY_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      outline={values.groupBy !== option.value}
                      onClick={() => validation.setFieldValue('groupBy', option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
              {rangeError && <div className="text-danger font-size-12 mt-2">{rangeError}</div>}
            </div>
          </div>
        </Card>

        <div className={styles.Cards}>
          {SUMMARY_CARDS.map((card) => (
            <Card className={styles.SummaryCard} key={card.key}>
              <div className={styles.CardLabel}>{card.label}</div>
              <div className={styles.CardValue}>{formatValue(summary[card.key], card.isMoney)}</div>
              {card.orderCountKey && summary[card.orderCountKey] !== undefined && (
                <div className={styles.CardNote}>
                  {`(${formatValue(summary[card.orderCountKey])} đơn)`}
                </div>
              )}
            </Card>
          ))}
          {REVENUE_CHANGE_COLUMNS.map((column) => (
            <Card className={styles.SummaryCard} key={column.key}>
              <div className={styles.CardLabel}>{column.label}</div>
              <div className={styles.CardValue}>
                {formatRevenueChangeCell(summary, column, currency)}
              </div>
            </Card>
          ))}
        </div>
        <div className="text-muted font-size-12 mt-2">{REVENUE_CHANGE_NOTE}</div>

        <Card className={pageStyles.Content}>
          <div className={styles.TableHeader}>
            <Label className={styles.TableTotal}>{`${rows.length} dòng`}</Label>
            <Button
              outline
              classes={styles.ExportButton}
              color={themeColor.LIGHT}
              disabled={!canExport}
              onClick={onExport}
            >
              <img src={DownloadIcon} alt="download" />
              <span>Xuất file</span>
            </Button>
          </div>

          <div className={pageStyles.TableContainer}>
            <table className={pageStyles.Table}>
              <thead>
                <tr>
                  {HEADS.map((head) => <th key={head}>{head}</th>)}
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
        </Card>
      </Container>
    </div>
  );
}

export default ManagerFlightRevenue;
